import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import {
  getProductDefaultInfo,
  getCartProductAvailabilityInfo,
  getCartProductInfo,
} from '../../../../utilities/selectors';
import { StockInfo } from '../../Product/StockInfo';
import { ProductPriceCartTable } from '../../Product/ProductPriceCartTable';
import {
  FlexCol,
  FlexRowCenterHorizontal,
  FlexRow,
  FlexRowCenterVertical,
} from '../../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../../Defaults/Text';
import { FormInput } from '../../Form/FormInput';
import { CustomNrStock } from './CustomNrStock';
import { __ } from 'react-i18n';
import { MobileTitleValue } from './MobileTitleValue';
import {
  getSymbolFromCurrency,
  formatPriceToString,
} from '../../../../utilities';
import { MinAmountInfoText } from './MinAmountInfoText';
import {
  getZemplinCartProductInfo,
  getZemplinTrueCount,
} from '../../../Zemplin/selectors';
import { CountSetter } from '../../../Zemplin/ProductDetail/CountSetter';
import { SvgIcon, IconType } from '../../../Zemplin/General/SvgIcon';
import { TableRow } from '../../../Zemplin/General/Table';

export interface Props {
  product: any;
  addToCart: any;
  removeFromCart: any;
  theme: any;
  hasMinAmountInfo?: boolean;
  isB2B: boolean;
}

function ItemsTabRowMobile({
  product,
  addToCart,
  theme,
  hasMinAmountInfo,
  removeFromCart,
  isB2B,
}: Props) {
  const {
    currency,
    picture,
    url,
    name,
    ordernr: currentOrdernr,
    productId,
    goodId,
  } = getCartProductInfo(product);
  const {
    unit,
    priceForPiece,
    priceForPieceWithoutVat,
    storesInfo,
    sumPrice,
    sumPriceWithoutVat,
    finalPricePerUnitWithVat,
    finalPricePerUnitWithoutVat,
  } = getZemplinCartProductInfo(product);
  const {
    count: initialCount,
    productPackages,
    onStock,
  } = getZemplinCartProductInfo(product);
  const isMks = unit === 'Mks';
  const step = productPackages ? productPackages : isMks ? 0.001 : 1;
  const [count, setCount] = React.useState(initialCount.toString());
  const { color, availText } = getCartProductAvailabilityInfo(
    product,
    isMks ? initialCount * 1000 : initialCount,
    onStock,
  );
  const correctSumPrice = isB2B
    ? (+sumPriceWithoutVat).toFixed(2)
    : (+sumPrice).toFixed(2);
  const trueAllPrice = isB2B
    ? (isMks
        ? +finalPricePerUnitWithoutVat * 1000
        : +finalPricePerUnitWithoutVat
      ).toFixed(2)
    : (isMks
        ? +finalPricePerUnitWithVat * 1000
        : +finalPricePerUnitWithVat
      ).toFixed(2);
  const priceWithoutVatString =
    onStock > 0 ? `${trueAllPrice} ${getSymbolFromCurrency(currency)}` : '-';
  const priceString =
    onStock > 0 ? formatPriceToString(correctSumPrice, currency) : '-';
  const minAmount = isMks ? 0.001 : 1;

  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.replace(',', '.');
    let newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    if (shouldAddToCart) {
      if (parseFloat(newCount) < minAmount) {
        newCount = minAmount.toFixed(3);
      }
      newCountNumber = parseFloat(newCount);
      const countDiff = -(initialCount - newCountNumber);
      const correctCount = getZemplinTrueCount(unit === 'Mks', countDiff);
      addToCart(product, correctCount);
    }

    setCount(newCount);
  };

  const handleRemoveClick = () => {
    removeFromCart(productId, goodId);
  };

  return (
    <Wrapper noBorderBottom={hasMinAmountInfo}>
      <UpperWrapper>
        <ImageWrapper to={url}>
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
        </ImageWrapper>
        <NameInputWrapper>
          <NameLink to={url}>
            <Name>{name}</Name>
          </NameLink>
          <CustomNrStock
            customNr={currentOrdernr}
            stockReadable={''}
            unit={unit}
            availText={''}
            storesInfo={storesInfo}
          />
        </NameInputWrapper>
      </UpperWrapper>
      <PriceValuesWrapper>
        <MobileTitleValue
          title={__('Cena bez DPH')}
          value={priceWithoutVatString}
        />
        <MobileTitleValue title={__('Spolu bez DPH')} value={priceString} />
        {productPackages > 0 && (
          <MobileTitleValue
            title={__('Balenie')}
            value={`${productPackages} ${unit}`}
          />
        )}
      </PriceValuesWrapper>
      <BottomWrapper>
        <CountSetter
          onChange={handleCountChange}
          unit={unit}
          count={count}
          step={step}
        />
        <SvgIcon
          onClick={handleRemoveClick}
          width={16}
          height={16}
          icon={IconType.trash}
          viewBox="0 0 16 16"
          alt="Odstrániť produkt"
          title="Odstrániť produkt"
          fill={theme.colors.textPrimary}
          cursor={'pointer'}
        />
      </BottomWrapper>
      <CustomNrStock
        customNr={''}
        stockReadable={availText}
        textColor={color}
        unit={unit}
        availText={availText}
        storesInfo={storesInfo}
      />
      {hasMinAmountInfo && <MinAmountInfoText borderBottom={true} />}
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)<{ noBorderBottom: boolean }>`
  width: 100%;
  max-width: ${rem(320)};
  margin: 0 auto;
  border-bottom: ${({ noBorderBottom, theme }) =>
    !noBorderBottom ? theme.borders.primary : 'none'};
  padding: ${rem(24)} 0;

  ${({ theme }) => theme.mediab.s450`
      margin: 0 -${rem(16)};
      padding:  ${rem(24)} ${rem(16)};
      max-width: initial;
      width: calc(100vw);
  `}
`;

const UpperWrapper = styled(FlexRow)`
  margin-bottom: ${rem(24)};
`;

const BottomWrapper = styled(FlexRowCenterVertical)`
  margin-bottom: ${rem(16)};
  justify-content: space-between;
  max-width: ${rem(320)};

  ${({ theme }) => theme.mediab.s450`
      max-width: initial;
  `}
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)`
  max-width: initial;
`;

const NameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(4)};

  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondaryLight};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(400)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(200)};
  `}
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const PriceValuesWrapper = styled(FlexCol)`
  margin-bottom: ${rem(16)};
`;

const WithTheme: any = withTheme(ItemsTabRowMobile);
export { WithTheme as ItemsTabRowMobile, StyledTableRow };
